/**
 * Value-based diffing for order / leg update payloads.
 *
 * The edit dialogs build a full column payload on every save. Writing columns
 * the dispatcher never touched makes server-side rules (the post-in_transit
 * tractor lock, RLS, triggers) fire on no-op values — e.g. adding an operator
 * to an in-transit order was rejected with "Cannot change tractor" because the
 * parent's tractor mirror was rewritten from leg 1.
 *
 * `diffUpdate` keeps only keys whose value actually differs from what's stored.
 */

/** Normalize a value for comparison: blank strings and undefined become null. */
function norm(v: unknown): unknown {
  if (v === undefined) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return v;
}

const ISO_LIKE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/** True when both values represent the same timestamp instant. */
function sameInstant(a: unknown, b: unknown): boolean {
  if (typeof a !== "string" || typeof b !== "string") return false;
  if (!ISO_LIKE.test(a) || !ISO_LIKE.test(b)) return false;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  return Number.isFinite(ta) && Number.isFinite(tb) && ta === tb;
}

export function valuesEqual(a: unknown, b: unknown): boolean {
  const na = norm(a);
  const nb = norm(b);
  if (na === nb) return true;
  if (na === null || nb === null) return false;
  if (sameInstant(na, nb)) return true;
  if (typeof na === "object" || typeof nb === "object") {
    try {
      return JSON.stringify(na) === JSON.stringify(nb);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Return only the entries of `next` whose value differs from `current`.
 * Keys missing from `current` are always kept (we can't prove they're no-ops).
 */
export function diffUpdate<T extends Record<string, unknown>>(
  current: Record<string, unknown> | null | undefined,
  next: T,
  /** Keys that must always be written even when unchanged. */
  alwaysInclude: string[] = [],
): Partial<T> {
  if (!current) return { ...next };
  const keep = new Set(alwaysInclude);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    if (keep.has(k) || !(k in current) || !valuesEqual(current[k], v)) {
      out[k] = v;
    }
  }
  return out as Partial<T>;
}
